import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { Save, Send, HelpOutline } from '@mui/icons-material';
import { getEmailSettings, updateEmailSettings, testEmailSettings } from '../api';

export default function EmailSettings({ embedded = false }) {
  const [settings, setSettings] = useState({
    email_provider: 'smtp',
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: 'HourTrax',
    enabled: false,
    use_sendmail: false,
    resend_api_key: '',
    resend_from_domain: '',
    graph_tenant_id: '',
    graph_client_id: '',
    graph_client_secret: '',
    graph_sender: '',
    gmail_client_email: '',
    gmail_private_key: '',
    gmail_sender: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasExistingPassword, setHasExistingPassword] = useState(false);
  const [hasResendKey, setHasResendKey] = useState(false);
  const [hasGraphSecret, setHasGraphSecret] = useState(false);
  const [hasGmailKey, setHasGmailKey] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await getEmailSettings();
      if (res.data) {
        setSettings({
          email_provider: res.data.email_provider || 'smtp',
          smtp_host: res.data.smtp_host || '',
          smtp_port: res.data.smtp_port || 587,
          smtp_secure: res.data.smtp_secure || false,
          smtp_user: res.data.smtp_user || '',
          smtp_password: '',
          from_email: res.data.from_email || '',
          from_name: res.data.from_name || 'HourTrax',
          enabled: res.data.enabled || false,
          use_sendmail: res.data.use_sendmail || false,
          resend_api_key: '',
          resend_from_domain: res.data.resend_from_domain || '',
          graph_tenant_id: res.data.graph_tenant_id || '',
          graph_client_id: res.data.graph_client_id || '',
          graph_client_secret: '',
          graph_sender: res.data.graph_sender || '',
          gmail_client_email: res.data.gmail_client_email || '',
          gmail_private_key: '',
          gmail_sender: res.data.gmail_sender || '',
        });
        setHasExistingPassword(res.data.hasPassword || false);
        setHasResendKey(res.data.hasResendKey || false);
        setHasGraphSecret(res.data.hasGraphSecret || false);
        setHasGmailKey(res.data.hasGmailKey || false);
      }
    } catch (err) {
      setError('Failed to load email settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProviderChange = (e, newProvider) => {
    if (newProvider !== null) {
      setSettings(prev => ({ ...prev, email_provider: newProvider }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await updateEmailSettings(settings);
      setSuccess('Email settings saved successfully');
      if (settings.smtp_password) {
        setHasExistingPassword(true);
        setSettings(prev => ({ ...prev, smtp_password: '' }));
      }
      if (settings.resend_api_key) {
        setHasResendKey(true);
        setSettings(prev => ({ ...prev, resend_api_key: '' }));
      }
      if (settings.graph_client_secret) {
        setHasGraphSecret(true);
        setSettings(prev => ({ ...prev, graph_client_secret: '' }));
      }
      if (settings.gmail_private_key) {
        setHasGmailKey(true);
        setSettings(prev => ({ ...prev, gmail_private_key: '' }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const res = await testEmailSettings();
      if (res.data.success) {
        setSuccess('Email configuration test successful!');
      } else {
        setError(res.data.error || 'Email test failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to test email configuration');
    } finally {
      setTesting(false);
    }
  };

  const isResend = settings.email_provider === 'resend';
  const isGraph = settings.email_provider === 'graph';
  const isGmail = settings.email_provider === 'gmail';

  const canTest = isGraph
    ? (settings.graph_tenant_id && settings.graph_client_id && (hasGraphSecret || settings.graph_client_secret))
    : isResend
    ? (hasResendKey || settings.resend_api_key)
    : isGmail
    ? (settings.gmail_client_email && settings.gmail_sender && (hasGmailKey || settings.gmail_private_key))
    : (settings.use_sendmail || settings.smtp_host);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const content = (
    <>
      <FormControlLabel
          control={
            <Switch
              checked={settings.enabled}
              onChange={handleChange}
              name="enabled"
            />
          }
          label="Enable Email Notifications"
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          When enabled, the system will send email notifications for new accounts, password resets, and account changes.
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Provider Selection */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Email Provider</Typography>
            <Tooltip title="Microsoft Graph: send via your own M365 tenant (free, included in M365). Google Workspace: send via Gmail API using a service account (free, included in Workspace). Resend: HTTP API with 3k/mo free tier. SMTP: direct connection to a mail server (may be blocked by cloud hosts like DigitalOcean).">
              <HelpOutline sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
          <ToggleButtonGroup
            value={settings.email_provider}
            exclusive
            onChange={handleProviderChange}
            size="small"
          >
            <ToggleButton value="graph">
              Microsoft 365 (Graph)
            </ToggleButton>
            <ToggleButton value="gmail">
              Google Workspace (Gmail)
            </ToggleButton>
            <ToggleButton value="resend">
              Resend (API)
            </ToggleButton>
            <ToggleButton value="smtp">
              SMTP Server
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Graph Settings */}
        {isGraph && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Azure App Registration Setup:</Typography>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>Go to <strong>Azure Portal</strong> &rarr; <strong>Entra ID</strong> &rarr; App Registrations &rarr; <strong>New Registration</strong></li>
                <li>Name: <strong>HourTrax Mailer</strong> (or any name)</li>
                <li>Supported account types: <strong>Accounts in this organizational directory only (Single tenant)</strong></li>
                <li>Leave Redirect URI blank (not needed for email) &rarr; click <strong>Register</strong></li>
                <li>Go to <strong>Certificates &amp; Secrets</strong> &rarr; <strong>New client secret</strong> &rarr; copy the <strong>Value</strong> (not the Secret ID)</li>
                <li>Go to <strong>API Permissions</strong> &rarr; <strong>Add a permission</strong> &rarr; <strong>Microsoft Graph</strong> &rarr; <strong>Application permissions</strong> &rarr; search <strong>Mail.Send</strong> &rarr; Add</li>
                <li>Click <strong>Grant admin consent for [your tenant]</strong> — status should show a green checkmark</li>
                <li>Copy the <strong>Application (Client) ID</strong> and <strong>Directory (Tenant) ID</strong> from the app's Overview page</li>
                <li>Create a <strong>shared mailbox</strong> in M365 Admin Center (Teams &amp; groups &rarr; Shared mailboxes &rarr; Add) — e.g. <strong>noreply@yourdomain.com</strong>. Free, no license needed</li>
              </ol>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Sends via Microsoft Graph API over HTTPS — no SMTP ports needed. 10,000 messages/day limit. Shared mailboxes do not require a license.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Tooltip title="Your Azure AD Directory (Tenant) ID. Found in Azure Portal > Entra ID > Overview.">
                  <TextField
                    fullWidth
                    label="Directory (Tenant) ID"
                    name="graph_tenant_id"
                    value={settings.graph_tenant_id}
                    onChange={handleChange}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    helperText="From Entra ID > Overview"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="The Application (Client) ID from your Azure App Registration. Found on the app's Overview page.">
                  <TextField
                    fullWidth
                    label="Application (Client) ID"
                    name="graph_client_id"
                    value={settings.graph_client_id}
                    onChange={handleChange}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    helperText="From App Registration > Overview"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="Client Secret value from your App Registration. Only needs to be entered once — it's stored securely and not displayed again.">
                  <TextField
                    fullWidth
                    type="password"
                    label="Client Secret"
                    name="graph_client_secret"
                    value={settings.graph_client_secret}
                    onChange={handleChange}
                    placeholder={hasGraphSecret ? '••••••••' : 'Enter client secret value'}
                    helperText={hasGraphSecret ? 'Leave blank to keep existing secret' : 'From Certificates & Secrets'}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="The M365 mailbox that emails will be sent from. Use a shared mailbox (free, no license needed) like noreply@yourdomain.com for a clean sending identity.">
                  <TextField
                    fullWidth
                    label="Sender Mailbox"
                    name="graph_sender"
                    value={settings.graph_sender}
                    onChange={handleChange}
                    placeholder="noreply@yourcompany.com"
                    helperText="Shared mailbox or licensed user"
                  />
                </Tooltip>
              </Grid>
            </Grid>
          </>
        )}

        {/* Gmail Settings */}
        {isGmail && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Google Cloud + Workspace Setup:</Typography>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                <li>Go to <strong>console.cloud.google.com</strong> &rarr; create or select a project</li>
                <li><strong>APIs &amp; Services</strong> &rarr; <strong>Library</strong> &rarr; enable <strong>Gmail API</strong></li>
                <li><strong>APIs &amp; Services</strong> &rarr; <strong>Credentials</strong> &rarr; <strong>Create Credentials</strong> &rarr; <strong>Service account</strong> (leave IAM roles empty)</li>
                <li>Open the service account &rarr; <strong>Keys</strong> &rarr; <strong>Add Key</strong> &rarr; <strong>JSON</strong> &rarr; download the key file</li>
                <li>Go to <strong>admin.google.com</strong> &rarr; <strong>Security</strong> &rarr; <strong>Access and data control</strong> &rarr; <strong>API controls</strong> &rarr; <strong>Manage domain-wide delegation</strong></li>
                <li><strong>Add new</strong> &rarr; paste the service account's <strong>Client ID</strong> (numeric, from the JSON key) &rarr; OAuth scope: <code>https://www.googleapis.com/auth/gmail.send</code></li>
                <li>Create or use a Workspace mailbox (e.g. <strong>noreply@yourdomain.com</strong>) — this is the sender</li>
                <li>Open the JSON key file: copy <code>client_email</code>, the full <code>private_key</code> (including BEGIN/END lines), and enter the sender email below</li>
              </ol>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Sends via Gmail API over HTTPS — no SMTP ports needed. Free with Google Workspace, no additional licensing.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Tooltip title="The service account email from the JSON key file (the 'client_email' field). Looks like xxx@project-name.iam.gserviceaccount.com">
                  <TextField
                    fullWidth
                    label="Service Account Email"
                    name="gmail_client_email"
                    value={settings.gmail_client_email}
                    onChange={handleChange}
                    placeholder="hourtrax-mailer@my-project.iam.gserviceaccount.com"
                    helperText="client_email from the JSON key"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="The Workspace mailbox that Gmail will send as. Must exist in your Workspace domain. The service account impersonates this user via domain-wide delegation.">
                  <TextField
                    fullWidth
                    label="Sender Mailbox"
                    name="gmail_sender"
                    value={settings.gmail_sender}
                    onChange={handleChange}
                    placeholder="noreply@yourcompany.com"
                    helperText="Workspace user the email is sent as"
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Tooltip title="The full 'private_key' field from the JSON key file, including the BEGIN PRIVATE KEY and END PRIVATE KEY lines. Only needs to be entered once — stored securely and not displayed again.">
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={10}
                    type={hasGmailKey && !settings.gmail_private_key ? 'password' : 'text'}
                    label="Service Account Private Key"
                    name="gmail_private_key"
                    value={settings.gmail_private_key}
                    onChange={handleChange}
                    placeholder={hasGmailKey ? '••••••••  (leave blank to keep existing key)' : '-----BEGIN PRIVATE KEY-----\nMIIEvQIBADANBgkqhkiG9w0BAQEFAASCBKcwggSjAgEAAoIBAQC...\n-----END PRIVATE KEY-----'}
                    helperText={hasGmailKey ? 'Leave blank to keep existing key' : 'Paste the private_key field from the JSON key file'}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                  />
                </Tooltip>
              </Grid>
            </Grid>
          </>
        )}

        {/* Resend Settings */}
        {isResend && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Resend sends email via HTTP API — no SMTP ports needed. Get your API key at{' '}
              <strong>resend.com/api-keys</strong>. Add and verify your sending domain at{' '}
              <strong>resend.com/domains</strong>.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Resend API Key"
                  name="resend_api_key"
                  value={settings.resend_api_key}
                  onChange={handleChange}
                  placeholder={hasResendKey ? '••••••••' : 're_xxxxxxxxxxxx'}
                  helperText={hasResendKey ? 'Leave blank to keep existing key' : 'Your Resend API key'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Tooltip title="The domain you verified in Resend (e.g. hourtrax.com). Used as the default sending domain if no From Email is set.">
                  <TextField
                    fullWidth
                    label="Sending Domain"
                    name="resend_from_domain"
                    value={settings.resend_from_domain}
                    onChange={handleChange}
                    placeholder="hourtrax.com"
                    helperText="Verified domain in Resend"
                  />
                </Tooltip>
              </Grid>
            </Grid>
          </>
        )}

        {/* SMTP / Sendmail Settings */}
        {!isResend && !isGraph && !isGmail && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.use_sendmail}
                  onChange={handleChange}
                  name="use_sendmail"
                />
              }
              label="Use Sendmail (Local Server)"
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Use the server's local mail system instead of external SMTP. This allows custom From addresses without SMTP restrictions.
            </Typography>

            {!settings.use_sendmail && (
              <>
                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" sx={{ mb: 2 }}>SMTP Server Settings</Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="SMTP Host"
                      name="smtp_host"
                      value={settings.smtp_host}
                      onChange={handleChange}
                      placeholder="smtp.example.com"
                      helperText="Your email server hostname"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="SMTP Port"
                      name="smtp_port"
                      value={settings.smtp_port}
                      onChange={handleChange}
                      helperText="Usually 587 (TLS) or 465 (SSL)"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.smtp_secure}
                          onChange={handleChange}
                          name="smtp_secure"
                        />
                      }
                      label="Use SSL/TLS (secure connection)"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Username"
                      name="smtp_user"
                      value={settings.smtp_user}
                      onChange={handleChange}
                      placeholder="user@example.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="password"
                      label="SMTP Password"
                      name="smtp_password"
                      value={settings.smtp_password}
                      onChange={handleChange}
                      placeholder={hasExistingPassword ? '••••••••' : 'Enter password'}
                      helperText={hasExistingPassword ? 'Leave blank to keep existing password' : ''}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>Sender Information</Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="From Email Address"
              name="from_email"
              value={settings.from_email}
              onChange={handleChange}
              placeholder={
                isGraph ? (settings.graph_sender || 'noreply@yourdomain.com')
                : isGmail ? (settings.gmail_sender || 'noreply@yourdomain.com')
                : isResend ? `noreply@${settings.resend_from_domain || 'yourdomain.com'}`
                : 'noreply@example.com'
              }
              helperText={(isGraph || isGmail) ? 'Leave blank to use the Sender Mailbox above' : 'Email address that will appear in the From field'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="From Name"
              name="from_name"
              value={settings.from_name}
              onChange={handleChange}
              placeholder="HourTrax"
              helperText="Name that will appear in the From field"
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={handleSave}
            disabled={saving}
          >
            Save Settings
          </Button>
          <Button
            variant="outlined"
            startIcon={testing ? <CircularProgress size={20} /> : <Send />}
            onClick={handleTest}
            disabled={testing || !canTest}
          >
            {isGraph ? 'Verify Graph Credentials' : isGmail ? 'Verify Gmail Credentials' : isResend ? 'Verify API Key' : 'Test Connection'}
          </Button>
        </Box>
    </>
  );

  if (embedded) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Send sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Email Configuration</Typography>
          <Tooltip title="Configure how your organization sends email notifications (new accounts, password resets, approvals). Choose Microsoft 365 Graph, Google Workspace Gmail, Resend, or SMTP.">
            <HelpOutline sx={{ ml: 1, fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
        {content}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Email Settings</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Paper sx={{ p: 3 }}>
        {content}
      </Paper>
    </Box>
  );
}
